import { useCallback, useState } from "react";
import { ScheduleLinkedSourceConflictErrorBody } from "@common/schemas/domains/accounting/scheduleLinkedSource.schema";
import { ScheduleLinkedConflictModal } from "./ScheduleLinkedConflictModal";
import {
    ScheduleLinkedConflictContext,
    ScheduleLinkedConflictOption,
    ScheduleLinkedConflictView,
    buildScheduleLinkedConflictView,
} from "./scheduleLinkedConflictCopy";

interface OpenConflictState {
    view: ScheduleLinkedConflictView;
    /** Resolves to whether the choice went through; the modal stays open otherwise. */
    onChoose: (option: ScheduleLinkedConflictOption) => Promise<boolean>;
}

/**
 * Local modal state for a linked-source conflict. Returned as a node rather
 * than opened as a global overlay because the match drawers are themselves
 * overlays, and only one overlay is shown at a time.
 */
export function useScheduleLinkedConflict() {
    const [state, setState] = useState<OpenConflictState | null>(null);
    const [isOpen, setIsOpen] = useState(false);

    const openLinkedConflict = useCallback(
        (options: {
            conflict: ScheduleLinkedSourceConflictErrorBody;
            context: ScheduleLinkedConflictContext;
            onChoose: OpenConflictState["onChoose"];
        }) => {
            setState({
                view: buildScheduleLinkedConflictView(options),
                onChoose: options.onChoose,
            });
            setIsOpen(true);
        },
        [],
    );

    const linkedConflictModal = state ? (
        <ScheduleLinkedConflictModal
            show={isOpen}
            view={state.view}
            onConfirm={async (option) => {
                const isResolved = await state.onChoose(option);
                if (isResolved) {
                    setIsOpen(false);
                }
            }}
            onBack={() => setIsOpen(false)}
            onExited={() => setState(null)}
        />
    ) : null;

    return { openLinkedConflict, linkedConflictModal };
}
